import React, { useEffect, useState } from 'react';

import {
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Switch,
  message,
} from 'antd';
import PhoneInput from 'antd-phone-input';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import { Company } from 'models/Company';
import { Country } from 'models/Country';
import { ItemInterface } from 'models/ItemInterface';
import { Role } from 'models/Role';
import { User } from 'models/User';

import { UserService } from 'services/UserService';

import appI18n from '../../../../config/i18n';
import { USER_TYPES } from '../SuperUsers';

interface DetailUserModalProps {
  isOpen: boolean;
  id: number;
  handleModalCancel: () => void;
  submit: (
    id: number,
    name: string,
    lastname: string,
    email: string,
    dialCode: string | null,
    phone: string | null,
    roleIds: number[],
    restrictedToCountries: boolean,
    allowedCountries: string[]
  ) => void;
  roles: Role[];
  defaultCountryCode: string;
  countries: Country[];
  getDialCode: (countryCode: string) => string;
}

function DetailUserModal({
  isOpen,
  handleModalCancel,
  submit,
  roles,
  id,
  defaultCountryCode,
  countries,
  getDialCode,
}: DetailUserModalProps) {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company>();
  const [userType, setUserType] = useState<string | undefined>(undefined);
  const [restrictedToCountries, setRestrictedToCountries] = useState(false);
  const { t } = useTranslation(undefined, { i18n: appI18n });

  useEffect(() => {
    form.resetFields();
    form.setFieldValue('phone', {
      ...form.getFieldValue('phone'),
      countryCode: parseInt(defaultCountryCode),
    });
    setUserType(undefined);
    setRestrictedToCountries(false);

    if (isOpen) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [isOpen]);

  const fetchData = async () => {
    setLoading(true);

    const response = await UserService.findById(id);

    if (response.success) {
      const data = response.data as User;
      form.setFieldValue('name', data.name);
      form.setFieldValue('company', data.company.name);
      form.setFieldValue('lastname', data.lastname);
      form.setFieldValue('email', data.email);
      form.setFieldValue(
        'roleIds',
        data.roles.map((record) => {
          return record.id;
        })
      );

      setSelectedCompany(data.company);
      setUserType(data.userType);

      const isRestricted = data.restrictedToCountries ?? false;
      setRestrictedToCountries(isRestricted);

      if (isRestricted && data.allowedCountries?.length > 0) {
        form.setFieldValue('allowedCountries', data.allowedCountries);
      }

      if (data.phone && data.dialCode) {
        const newPhone = {
          ...form.getFieldValue('phone'),
          countryCode: parseInt(data.dialCode),
          areaCode: null,
          phoneNumber: null,
        };

        if (data.phone) {
          newPhone.areaCode = data.phone;
        }

        form.setFieldValue('phone', newPhone);
      } else {
        form.setFieldValue('phone', {
          ...form.getFieldValue('phone'),
          countryCode: parseInt(getDialCode(data.company.countryCode)),
        });
      }

      setLoading(false);
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t(
            'scenes.settings.super_users.components.detail_user_modal.errors.fetch_user_failed'
          ),
        3.5
      );
      handleModalCancel();
    }

    setLoading(false);
  };

  const onFinish = async (values: any) => {
    setLoading(true);

    await submit(
      id,
      values.name,
      values.lastname,
      values.email,
      values.phone.phoneNumber === null ? null : `+${values.phone.countryCode}`,
      values.phone.phoneNumber === null
        ? null
        : `${values.phone.areaCode}${values.phone.phoneNumber}`,
      values.roleIds || [],
      restrictedToCountries,
      restrictedToCountries ? values.allowedCountries || [] : []
    );

    setLoading(false);
  };

  const getRoles = (): ItemInterface[] => {
    return roles
      .filter((record) => {
        return record.companyId === selectedCompany?.id;
      })
      .map((record) => {
        return {
          value: record.id,
          label: record.name,
        };
      });
  };

  const getUserTypeLabel = (type: string) => {
    switch (type) {
      case USER_TYPES.SUPER_ADMIN:
        return t(
          'scenes.settings.super_users.scene.table.user_type.super_admin'
        );
      case USER_TYPES.ADMIN:
        return t('scenes.settings.super_users.scene.table.user_type.admin');
      default:
        return type;
    }
  };

  const getCountryOptions = () => {
    return countries.map((record) => ({
      label: record.name,
      value: record.code,
    }));
  };

  const handleChangeRestrictedToCountries = (checked: boolean) => {
    setRestrictedToCountries(checked);
    if (!checked) {
      form.setFieldValue('allowedCountries', []);
    }
  };

  return (
    <>
      {contextHolder}

      <Modal
        title={t(
          'scenes.settings.super_users.components.detail_user_modal.title',
          { id }
        )}
        open={isOpen}
        onCancel={handleModalCancel}
        maskClosable={false}
        destroyOnClose
        footer={null}
        width={480}
      >
        <Divider style={{ marginTop: '15px', marginBottom: '15px' }} />

        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                label={t(
                  'scenes.settings.super_users.components.detail_user_modal.labels.user_type'
                )}
              >
                <Input
                  type="text"
                  readOnly
                  disabled={loading}
                  value={userType ? getUserTypeLabel(userType) : ''}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="company"
                label={t(
                  'scenes.settings.super_users.components.detail_user_modal.labels.company'
                )}
              >
                <Input
                  type="text"
                  disabled={loading}
                  readOnly
                  placeholder={t(
                    'scenes.settings.super_users.components.detail_user_modal.placeholders.company'
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="email"
                label={t(
                  'scenes.settings.super_users.components.detail_user_modal.labels.email'
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      'scenes.settings.super_users.components.detail_user_modal.validations.email.required'
                    ),
                  },
                  {
                    type: 'email',
                    message: t(
                      'scenes.settings.super_users.components.detail_user_modal.validations.email.invalid'
                    ),
                  },
                ]}
              >
                <Input
                  type="text"
                  disabled={loading}
                  placeholder={t(
                    'scenes.settings.super_users.components.detail_user_modal.placeholders.email'
                  )}
                  addonAfter={'@'}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="name"
                label={t(
                  'scenes.settings.super_users.components.detail_user_modal.labels.name'
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      'scenes.settings.super_users.components.detail_user_modal.validations.name.required'
                    ),
                  },
                ]}
              >
                <Input
                  type="text"
                  disabled={loading}
                  placeholder={t(
                    'scenes.settings.super_users.components.detail_user_modal.placeholders.name'
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="lastname"
                label={t(
                  'scenes.settings.super_users.components.detail_user_modal.labels.lastname'
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      'scenes.settings.super_users.components.detail_user_modal.validations.lastname.required'
                    ),
                  },
                ]}
              >
                <Input
                  type="text"
                  disabled={loading}
                  placeholder={t(
                    'scenes.settings.super_users.components.detail_user_modal.placeholders.lastname'
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24}>
              <Form.Item
                name="phone"
                label={t(
                  'scenes.settings.super_users.components.detail_user_modal.labels.phone'
                )}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }

                      if (value.valid()) {
                        return Promise.resolve();
                      }

                      if (!value.valid() && value.phoneNumber === null) {
                        return Promise.resolve();
                      }

                      return Promise.reject(
                        new Error(
                          t(
                            'scenes.settings.super_users.components.detail_user_modal.validations.phone.invalid'
                          )
                        )
                      );
                    },
                  },
                ]}
              >
                <PhoneInput
                  enableSearch
                  searchNotFound={t(
                    'scenes.settings.super_users.components.detail_user_modal.phone.search_not_found'
                  )}
                  searchPlaceholder={t(
                    'scenes.settings.super_users.components.detail_user_modal.phone.search_placeholder'
                  )}
                  allowClear
                  placeholder={t(
                    'scenes.settings.super_users.components.detail_user_modal.placeholders.phone'
                  )}
                  disabled={loading}
                  onChange={(v) => {
                    if (!v.countryCode && !v.phoneNumber && !v.areaCode) {
                      form.setFieldValue('phone', {
                        ...form.getFieldValue('phone'),
                        countryCode: selectedCompany
                          ? parseInt(getDialCode(selectedCompany.countryCode))
                          : parseInt(defaultCountryCode),
                      });
                    }
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="roleIds"
                label={t(
                  'scenes.settings.super_users.components.detail_user_modal.labels.roles'
                )}
              >
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  options={getRoles()}
                  disabled={loading}
                  placeholder={t(
                    'scenes.settings.super_users.components.detail_user_modal.placeholders.roles'
                  )}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>

          {userType === USER_TYPES.ADMIN && (
            <>
              <Row gutter={24}>
                <Col xs={24}>
                  <Form.Item
                    label={t(
                      'scenes.settings.super_users.components.detail_user_modal.labels.restricted_to_countries'
                    )}
                  >
                    <Switch
                      checked={restrictedToCountries}
                      onChange={handleChangeRestrictedToCountries}
                      disabled={loading}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {restrictedToCountries && (
                <Row gutter={24}>
                  <Col xs={24}>
                    <Form.Item
                      name="allowedCountries"
                      label={t(
                        'scenes.settings.super_users.components.detail_user_modal.labels.allowed_countries'
                      )}
                      rules={[
                        {
                          validator: (_, value) => {
                            if (
                              restrictedToCountries &&
                              (!value || value.length === 0)
                            ) {
                              return Promise.reject(
                                new Error(
                                  t(
                                    'scenes.settings.super_users.components.detail_user_modal.validations.allowed_countries.min_one'
                                  )
                                )
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Checkbox.Group
                        options={getCountryOptions()}
                        disabled={loading}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </>
          )}

          <Row gutter={24}>
            <Col xs={24}>
              <Button
                onClick={() => {
                  form.submit();
                }}
                loading={loading}
                type="primary"
                block
              >
                {t(
                  'scenes.settings.super_users.components.detail_user_modal.buttons.save_changes'
                )}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default DetailUserModal;
